import { useEffect, useRef, useState } from "react";

/**
 * useReveal — tiny IntersectionObserver hook for tasteful scroll-reveal.
 * Returns [ref, inView]. Respects prefers-reduced-motion (reveals instantly).
 * No framer-motion dependency — pure CSS transitions drive the actual motion.
 */
export function useReveal(options = {}) {
    const ref = useRef(null);
    const reduced = typeof window !== "undefined"
        && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const [inView, setInView] = useState(!!reduced);

    useEffect(() => {
        if (reduced) { setInView(true); return undefined; }
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) { setInView(true); obs.unobserve(e.target); }
                });
            },
            { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? "0px 0px -10% 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [reduced, options.threshold, options.rootMargin]);

    return [ref, inView];
}

/** Reveal wrapper class helper — pass inView + optional stagger index. */
export function revealCls(inView, delayMs = 0) {
    return {
        style: { transitionDelay: inView ? `${delayMs}ms` : "0ms" },
        className: `transition-[opacity,transform] duration-700 ease-out will-change-[opacity,transform] ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`,
    };
}
